import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { money } from './range';

const Row = ({ r, onOpen }) => (
  <li className="flex items-start justify-between gap-3 py-3 border-b border-[#ecebe4] last:border-0" data-testid={`drill-row-${r.key}`}>
    <div className="min-w-0">
      <p className="text-sm font-medium text-[#20211d] truncate">{r.name}</p>
      <p className="text-[11px] text-[#77776e]">{r.date} · {r.source}{r.method ? ` · ${r.method}` : ''}{r.reference ? ` · ${r.reference}` : ''}</p>
      {r.linked_invoice_id && <p className="text-[11px] text-[#6f7d58]">Linked to an invoice, counted once here</p>}
    </div>
    <div className="text-right shrink-0">
      <p className={`text-sm font-semibold ${r.flow === 'out' ? 'text-[#a3471d]' : 'text-[#20211d]'}`}>{r.flow === 'out' ? '-' : ''}{money(r.amount_cents)}</p>
      {r.href && <button type="button" onClick={() => onOpen(r.href)} className="text-[11px] font-semibold text-[#b75b28] hover:underline" data-testid={`drill-open-${r.key}`}>Open ↗</button>}
    </div>
  </li>
);

export const RecordsDrawer = ({ drill, onClose }) => {
  const navigate = useNavigate();
  const rows = drill?.rows || [];
  const inSum = rows.filter(r => r.flow === 'in').reduce((n, r) => n + r.amount_cents, 0);
  const outSum = rows.filter(r => r.flow === 'out').reduce((n, r) => n + r.amount_cents, 0);
  const inRows = rows.filter(r => r.flow === 'in').length;
  const outRows = rows.filter(r => r.flow === 'out').length;
  const plural = (k) => `${k} ${k === 1 ? 'RECORD' : 'RECORDS'}`;
  return (
    <Sheet open={!!drill} onOpenChange={o => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-[#fffefa]" data-testid="analytics-drill-drawer">
        <SheetHeader>
          <p className="text-[10px] font-bold tracking-[0.1em] text-[#74776a]">ANALYTICS · RECORDS BEHIND THIS NUMBER</p>
          <SheetTitle data-testid="drill-title">{drill?.title}</SheetTitle>
        </SheetHeader>
        {drill?.kind === 'clients' ? (
          <ul className="mt-4" data-testid="drill-client-list">
            {rows.map(c => (
              <li key={c.id} className="flex justify-between py-3 border-b border-[#ecebe4] text-sm">
                <span>{c.full_name || 'Client'}</span>
                <button type="button" className="text-[11px] font-semibold text-[#b75b28]" onClick={() => navigate(`/admin/users/${c.id}`)}>Open ↗</button>
              </li>
            ))}
            {!rows.length && <p className="py-8 text-sm text-[#77776e]">No new clients in this interval.</p>}
          </ul>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-2" data-testid="drill-summary">
              <div className="rounded-[8px] border border-[#e7e4db] p-3"><p className="text-[10px] font-bold tracking-[0.08em] text-[#74776a]">MONEY IN · {plural(inRows)}</p><p className="text-xl font-medium" data-testid="drill-in-total">{money(inSum)}</p></div>
              <div className="rounded-[8px] border border-[#e7e4db] p-3"><p className="text-[10px] font-bold tracking-[0.08em] text-[#74776a]">MONEY OUT · {plural(outRows)}</p><p className="text-xl font-medium" data-testid="drill-out-total">{money(outSum)}</p></div>
            </div>
            {drill?.expectIn !== undefined && (
              <p className={`mt-2 text-[11px] ${drill.expectIn === inSum ? 'text-[#6f7d58]' : 'text-red-600'}`} data-testid="drill-reconcile">
                {drill.expectIn === inSum ? `Matches the ${money(drill.expectIn)} shown on the chart.` : `Does not match the chart (${money(drill.expectIn)}). Please report this.`}
              </p>
            )}
            <ul className="mt-3">{rows.map(r => <Row key={r.key} r={r} onOpen={(h) => { onClose(); navigate(h); }} />)}</ul>
            {!rows.length && <p className="py-8 text-sm text-[#77776e]" data-testid="drill-empty">No recorded money in or out for this interval.</p>}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
